import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { businessInfo } from '../mock';
import SEO from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContactPage = () => {
  const [generalForm, setGeneralForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [generalLoading, setGeneralLoading] = useState(false);

  const [courseForm, setCourseForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [courseLoading, setCourseLoading] = useState(false);

  const handleGeneralChange = (e) => {
    setGeneralForm({
      ...generalForm,
      [e.target.name]: e.target.value
    });
  };

  const handleCourseChange = (e) => {
    setCourseForm({
      ...courseForm,
      [e.target.name]: e.target.value
    });
  };

  const handleGeneralSubmit = async (e) => {
    e.preventDefault();
    if (!generalForm.name || !generalForm.email || !generalForm.message) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setGeneralLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/enquiries/booking`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: generalForm.name,
          email: generalForm.email,
          phone: generalForm.phone || null,
          message: generalForm.message
        }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to submit enquiry');
      }
      
      toast.success('Message sent! We\'ll get back to you soon.');
      setGeneralForm({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Error submitting booking enquiry:', error);
      toast.error('Failed to send message. Please try again or call us directly.');
    } finally {
      setGeneralLoading(false);
    }
  };

  const handleCourseSubmit = async (e) => {
    e.preventDefault();
    if (!courseForm.name || !courseForm.email || !courseForm.message) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setCourseLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/enquiries/course`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: courseForm.name,
          email: courseForm.email,
          phone: courseForm.phone || null,
          message: courseForm.message
        }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to submit enquiry');
      }
      
      toast.success('Course enquiry received! We\'ll contact you with course dates and details.');
      setCourseForm({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Error submitting course enquiry:', error);
      toast.error('Failed to send enquiry. Please try again or call us directly.');
    } finally {
      setCourseLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream-50">
      <SEO 
        title="Contact Us - Book Dog Grooming Manchester"
        description="Contact Pups & Dreams Spa & Academy in Manchester. Book a grooming appointment or enquire about our dog grooming courses in Denton, Greater Manchester. Call 07592 222056."
        keywords="contact dog groomer Manchester, book dog grooming appointment Manchester, dog grooming enquiry Manchester, dog grooming course enquiry Manchester, book dog groomer Denton, Manchester dog grooming contact"
      />
      {/* Hero Section */}
      <section className="bg-white !pt-[80px] !pb-[0px]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-sage-900">
              Get in Touch
            </h1>
            <p className="text-xl leading-relaxed text-sage-700">
              Have questions? Want to book an appointment or enquire about our courses? 
              We'd love to hear from you!
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="border-sage-200 hover:shadow-lg transition-shadow bg-gradient-to-br from-white to-sage-50">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 bg-gradient-to-br from-sage-100 to-sage-200 flex items-center justify-center mx-auto mb-4 rounded-full">
                  <Phone className="text-black" size={24} />
                </div>
                <h3 className="font-bold text-sage-900 mb-2">Phone</h3>
                <a href={`tel:${businessInfo.phone}`} className="text-sage-700 hover:text-sage-900 transition-colors">
                  {businessInfo.phone}
                </a>
              </CardContent>
            </Card>

            <Card className="border-sage-200 hover:shadow-lg transition-shadow bg-gradient-to-br from-white to-sage-50">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 bg-gradient-to-br from-sage-100 to-sage-200 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="text-black" size={24} />
                </div>
                <h3 className="font-bold text-sage-900 mb-2">Email</h3>
                <a href={`mailto:${businessInfo.email}`} className="text-sage-700 hover:text-sage-900 transition-colors text-sm break-all">
                  {businessInfo.email}
                </a>
              </CardContent>
            </Card>

            <Card className="border-sage-200 hover:shadow-lg transition-shadow bg-gradient-to-br from-white to-sage-50">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 bg-gradient-to-br from-sage-100 to-sage-200 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapPin className="text-black" size={24} />
                </div>
                <h3 className="font-bold text-sage-900 mb-2">Address</h3>
                <p className="text-sage-700 text-sm">
                  {businessInfo.address.full}
                </p>
              </CardContent>
            </Card>

            <Card className="border-sage-200 hover:shadow-lg transition-shadow bg-gradient-to-br from-white to-sage-50">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 bg-gradient-to-br from-sage-100 to-sage-200 flex items-center justify-center mx-auto mb-4 rounded-full ">
                  <Clock className="text-black" size={24} />
                </div>
                <h3 className="font-bold text-sage-900 mb-2">Hours</h3>
                <div className="text-sage-700 text-sm space-y-1">
                  <p>Mon-Fri: 9am-6pm</p>
                  <p>Sat: 9am-3pm</p>
                  <p>Sun: Closed</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Forms Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <Tabs defaultValue="general" className="max-w-6xl mx-auto">
            <TabsList className="grid w-full grid-cols-2 mb-8">
              <TabsTrigger value="general" className="text-base">Booking Enquiry</TabsTrigger>
              <TabsTrigger value="course" className="text-base">Course Enquiry</TabsTrigger>
            </TabsList>

            {/* General Enquiry Form */}
            <TabsContent value="general">
              <Card className="border-sage-200">
                <CardHeader>
                  <CardTitle className="text-2xl text-sage-900">Send Us a Message</CardTitle>
                  <p className="text-sage-600">Have a question? We're here to help!</p>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleGeneralSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="gen-name" className="text-sage-900">Name *</Label>
                        <Input
                          id="gen-name"
                          name="name"
                          value={generalForm.name}
                          onChange={handleGeneralChange}
                          placeholder="Your name"
                          className="mt-1.5"
                          required />

                      </div>
                      <div>
                        <Label htmlFor="gen-phone" className="text-sage-900">Phone</Label>
                        <Input
                          id="gen-phone"
                          name="phone"
                          type="tel"
                          value={generalForm.phone}
                          onChange={handleGeneralChange}
                          placeholder="07xxx xxxxxx"
                          className="mt-1.5" />

                      </div>
                    </div>

                    <div>
                      <Label htmlFor="gen-email" className="text-sage-900">Email *</Label>
                      <Input
                        id="gen-email"
                        name="email"
                        type="email"
                        value={generalForm.email}
                        onChange={handleGeneralChange}
                        placeholder="your@email.com"
                        className="mt-1.5"
                        required />

                    </div>

                    <div>
                      <Label htmlFor="gen-message" className="text-sage-900">Message *</Label>
                      <Textarea
                        id="gen-message"
                        name="message"
                        value={generalForm.message}
                        onChange={handleGeneralChange}
                        placeholder="How can we help you?"
                        className="mt-1.5 min-h-[150px]"
                        required />

                    </div>

                    <div className="flex justify-start">
                      <Button
                        type="submit"
                        size="lg"
                        disabled={generalLoading}
                        className="inline-flex items-center justify-center whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:bg-primary/90 h-10 bg-gradient-to-r from-sage-600 to-sage-700 hover:from-sage-700 hover:to-sage-800 text-sm font-medium gap-2 px-8 rounded-md shadow text-white">
                        {generalLoading ? (
                          <>
                            <Loader2 className="mr-2 animate-spin" size={18} />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send className="mr-2" size={18} />
                            Send Message
                          </>
                        )}
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Course Enquiry Form */}
            <TabsContent value="course">
              <Card className="border-sage-200">
                <CardHeader>
                  <CardTitle className="text-2xl text-sage-900">Enquire About Our Courses</CardTitle>
                  <p className="text-sage-600">Interested in becoming a dog groomer? Let's talk!</p>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleCourseSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="course-name" className="text-sage-900">Name *</Label>
                        <Input
                          id="course-name"
                          name="name"
                          value={courseForm.name}
                          onChange={handleCourseChange}
                          placeholder="Your name"
                          className="mt-1.5"
                          required />

                      </div>
                      <div>
                        <Label htmlFor="course-phone" className="text-sage-900">Phone</Label>
                        <Input
                          id="course-phone"
                          name="phone"
                          type="tel"
                          value={courseForm.phone}
                          onChange={handleCourseChange}
                          placeholder="07xxx xxxxxx"
                          className="mt-1.5" />

                      </div>
                    </div>

                    <div>
                      <Label htmlFor="course-email" className="text-sage-900">Email *</Label>
                      <Input
                        id="course-email"
                        name="email"
                        type="email"
                        value={courseForm.email}
                        onChange={handleCourseChange}
                        placeholder="your@email.com"
                        className="mt-1.5"
                        required />

                    </div>

                    <div>
                      <Label htmlFor="course-message" className="text-sage-900">Tell Us About Yourself *</Label>
                      <Textarea
                        id="course-message"
                        name="message"
                        value={courseForm.message}
                        onChange={handleCourseChange}
                        placeholder="Why are you interested in dog grooming? Any experience with dogs?"
                        className="mt-1.5 min-h-[150px]"
                        required />

                    </div>

                    <div className="flex justify-start">
                      <Button
                        type="submit"
                        size="lg"
                        disabled={courseLoading}
                        className="inline-flex items-center justify-center whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:bg-primary/90 h-10 bg-gradient-to-r from-sage-600 to-sage-700 hover:from-sage-700 hover:to-sage-800 text-sm font-medium gap-2 px-8 rounded-md shadow text-white">
                        {courseLoading ? (
                          <>
                            <Loader2 className="mr-2 animate-spin" size={18} />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send className="mr-2" size={18} />
                            Send Enquiry
                          </>
                        )}
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-sage-900 mb-4">
                Find Us
              </h2>
              <p className="text-lg text-sage-600">
                Located on Manchester Road in Denton – easy to reach from across Greater Manchester
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-2xl border border-sage-200">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2377.8944699305636!2d-2.1185267!3d53.4555556!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x487bb3f3b3b3b3b3%3A0x3b3b3b3b3b3b3b3b!2s65%20Manchester%20Rd%2C%20Denton%2C%20Manchester%20M34%202AF%2C%20UK!5e0!3m2!1sen!2suk!4v1234567890123!5m2!1sen!2suk"
                width="100%"
                height="450"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Pups & Dreams Location">
              </iframe>
            </div>
            <div className="mt-8 text-center">
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=65+Manchester+Road+Denton+Manchester+M34+2AF+UK`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center bg-gradient-to-r from-sage-600 to-sage-700 hover:from-sage-700 hover:to-sage-800 hover:shadow-xl transition-all font-semibold gap-2 px-8 py-3 rounded-lg shadow-lg text-white">

                <MapPin size={20} />
                Get Directions
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>);

};

export default ContactPage;